import * as React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  Stack,
  Chip,
  useMediaQuery,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  Legend,
  Area,
  ComposedChart,
} from "recharts";

/** ---------- helpers ---------- */
const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : Number(n).toFixed(0));
const fmt1 = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : Number(n).toFixed(1));

function gttMin_to_mlHr(gttMin, dripFactorGttPerMl) {
  return (Number(gttMin) * 60) / Number(dripFactorGttPerMl);
}

/** ---------- demo IV drip data (1 point per minute) ---------- */
function makeDripDemo({ minutes = 60, target = 25 }) {
  const rand = (a, b) => a + Math.random() * (b - a);

  return Array.from({ length: minutes }).map((_, i) => {
    let gtt = target + rand(-3, 3);
    if (i > 18 && i < 28) gtt += rand(5, 10);
    if (i > 40 && i < 45) gtt = rand(0, 2);

    gtt = Math.max(0, Math.round(gtt));
    return {
      t: `${String(Math.floor(i / 60)).padStart(2, "0")}:${String(i % 60).padStart(2, "0")}`,
      gtt,
    };
  });
}

/** ---------- ICU Card ---------- */
function Card({ title, right, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 3,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
        {right}
      </Stack>
      <Divider sx={{ my: 1.5 }} />
      {children}
    </Paper>
  );
}

export default function IVDripICUGraph() {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  // ICU settings
  const [dripFactor] = React.useState(20); // 20 macro / 60 micro
  const targetMin = 20;
  const targetMax = 30;

  // demo data
  const [raw, setRaw] = React.useState(() => makeDripDemo({ minutes: 60, target: 25 }));

  const data = React.useMemo(() => {
    let cum = 0;
    return raw.map((r) => {
      const mlHr = gttMin_to_mlHr(r.gtt, dripFactor);
      const mlPerMin = mlHr / 60;
      cum += mlPerMin;
      return { ...r, mlHr, cumMl: cum };
    });
  }, [raw, dripFactor]);

  // summary
  const avgGtt = data.reduce((s, r) => s + r.gtt, 0) / data.length;
  const last = data[data.length - 1] || {};
  const totalMl = last?.cumMl ?? 0;

  // chart heights
  const chartH1 = isSm ? 320 : 460;
  const chartH2 = isSm ? 320 : 440;

  return (
    <Box sx={{ width: "100%", pb: 3 }}>
      {/* Top ICU Toolbar */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: 3,
          mb: 2,
          width: "100%",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              IV Drip Monitor (ICU)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drip rate • Flow • Cumulative volume (demo)
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip size="small" label={`Drip factor: ${dripFactor} gtt/ml`} />
            <Chip size="small" label={`Target: ${targetMin}-${targetMax} gtt/min`} />
            <Button
              size="small"
              variant="outlined"
              sx={{ borderRadius: 2 }}
              onClick={() => setRaw(makeDripDemo({ minutes: 60, target: 25 }))}
            >
              New Demo
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Average Rate
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              {fmt1(avgGtt)} gtt/min
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Target: {targetMin}-{targetMax} gtt/min
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Current Flow
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              {fmt1(last.mlHr)} ml/hr
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Auto-calculated from gtt/min
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Cumulative Infused
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              {fmt1(totalMl)} ml
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last 60 min (demo)
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* FULL WIDTH GRAPH 1 */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card
            title="IV Drip Rate (gtt/min) — ICU Trend"
            right={
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip size="small" label="Under = Alarm" />
                <Chip size="small" label="Over = Alarm" />
              </Stack>
            }
          >
            <Box sx={{ height: chartH1, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 10, right: 18, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="t" interval={isSm ? 10 : 5} />
                  <YAxis />
                  <ReferenceArea y1={targetMin} y2={targetMax} />
                  <ReferenceLine y={targetMin} />
                  <ReferenceLine y={targetMax} />
                  <Tooltip
                    formatter={(v, name) => {
                      if (name === "gtt") return [`${fmt(v)} gtt/min`, "Drip Rate"];
                      return [v, name];
                    }}
                    labelFormatter={(l) => `Time: ${l}`}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="gtt" strokeWidth={3} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Green band = target range. Below/above = alarm zone.
            </Typography>
          </Card>
        </Grid>

        {/* FULL WIDTH GRAPH 2 */}
        <Grid item xs={12}>
          <Card title="Flow (ml/hr) + Cumulative Volume (ml)">
            <Box sx={{ height: chartH2, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 10, right: 18, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="t" interval={isSm ? 10 : 6} />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip
                    formatter={(v, name) => {
                      if (name === "mlHr") return [`${fmt1(v)} ml/hr`, "Flow"];
                      if (name === "cumMl") return [`${fmt1(v)} ml`, "Cumulative"];
                      return [v, name];
                    }}
                  />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="mlHr" strokeWidth={3} dot={false} />
                  <Area yAxisId="right" type="monotone" dataKey="cumMl" />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Formula: ml/hr = (gtt/min × 60) / (gtt/ml)
            </Typography>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}